import { useMemo, useState } from "react";
import { Star, ShoppingBag, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { products } from "@/data/products";
import useReveal from "@/hooks/useReveal";

const filters = [
  { id: "all", label: "All" },
  { id: "running", label: "Running" },
  { id: "lifestyle", label: "Lifestyle" },
  { id: "training", label: "Training" },
];

function Stars({ rating }) {
  return (
    <div className="flex items-center gap-0.5" aria-label={`Rated ${rating} out of 5`}>
      {[0, 1, 2, 3, 4].map((i) => (
        <Star
          key={i}
          size={12}
          className={i < Math.round(rating) ? "star-active" : "star-inactive"}
        />
      ))}
    </div>
  );
}

function ProductCard({ p }) {
  const badgeClass =
    p.badge === "New" ? "chip-new" : p.badge === "Best Seller" ? "chip-best" : "chip-limited";
  return (
    <article className="product-card" data-testid={`product-card-${p.id}`}>
      <div className="relative w-full aspect-square rounded-xl bg-[#f5f3ee] overflow-hidden mb-5">
        <img
          src={p.image}
          alt={`${p.name} — ${p.descriptor}`}
          className="product-img-primary absolute inset-0 w-full h-full object-cover"
          loading="lazy"
        />
        <img
          src={p.altImage}
          alt=""
          aria-hidden="true"
          className="product-img-alt w-full h-full object-cover"
          loading="lazy"
        />
        {p.badge && <span className={`chip ${badgeClass} absolute top-3 left-3`}>{p.badge}</span>}
        <button
          data-testid={`quick-view-${p.id}`}
          className="quick-view"
          onClick={() => toast(`Quick view: ${p.name}`, { description: p.descriptor })}
        >
          Quick View
        </button>
      </div>

      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-display font-bold text-lg leading-tight">{p.name}</h3>
          <p className="text-sm text-black/60 mt-0.5">{p.descriptor}</p>
        </div>
        <div className="font-display font-bold text-lg whitespace-nowrap">${p.price}</div>
      </div>

      <div className="mt-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Stars rating={p.rating} />
          <span className="text-xs text-black/50 mono">{p.rating} · {p.reviews.toLocaleString()}</span>
        </div>
        <button
          data-testid={`add-to-cart-${p.id}`}
          onClick={() => toast.success(`${p.name} added to cart`, { description: `$${p.price} · 1 pair` })}
          className="w-9 h-9 rounded-full bg-[#0e0e10] text-white flex items-center justify-center hover:bg-[#ff5722] transition-colors"
          aria-label={`Add ${p.name} to cart`}
        >
          <ShoppingBag size={14} />
        </button>
      </div>
    </article>
  );
}

export default function ProductsGrid() {
  const [active, setActive] = useState("all");
  const ref = useReveal();

  const visible = useMemo(() => {
    if (active === "all") return products;
    return products.filter((p) => p.category === active);
  }, [active]);

  return (
    <section
      ref={ref}
      data-testid="products-section"
      className="reveal max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32"
      id="shop"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
        <div>
          <span className="mono text-xs uppercase tracking-widest text-[#ff5722]">Featured pairs</span>
          <h2 className="font-display font-extrabold text-4xl md:text-5xl lg:text-6xl leading-[0.95] mt-3 tracking-tight">
            The current lineup.
          </h2>
        </div>
        <div className="flex flex-wrap gap-2" role="tablist" aria-label="Filter products">
          {filters.map((f) => (
            <button
              key={f.id}
              role="tab"
              aria-selected={active === f.id}
              data-testid={`filter-${f.id}`}
              className={`filter-pill ${active === f.id ? "active" : ""}`}
              onClick={() => setActive(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
        {visible.map((p) => (
          <ProductCard key={p.id} p={p} />
        ))}
      </div>

      <div className="mt-12 flex justify-center">
        <a href="#collections" data-testid="view-all-products" className="btn-ghost">
          View All Products
          <ArrowRight size={16} />
        </a>
      </div>
    </section>
  );
}
